import React, { useState, useEffect } from 'react';
import axios from './utils/axiosConfig';
import { useAuth } from './context/AuthContext';
import {
  Container,
  Table,
  Button,
  Modal,
  Form,
  Alert,
  Badge,
  ButtonGroup,
  Row,
  Col,
  Card
} from 'react-bootstrap';

/**
 * 屆別管理組件
 * 提供各社屆別的新增、編輯、刪除功能
 */
function TermManagement() {
  const { user } = useAuth();
  const token = localStorage.getItem('token');

  // 狀態管理
  const [terms, setTerms] = useState([]);
  const [societies, setSocieties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // 篩選條件
  const [filterSsid, setFilterSsid] = useState('');

  // Modal 狀態
  const [showModal, setShowModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // add, edit
  const [currentTerm, setCurrentTerm] = useState({
    term_id: null,
    ssid: user?.e_ssid || '',
    term_no: '',
    term_name: '',
    start_date: '',
    end_date: '',
    is_active: 1
  });

  // 載入社別列表
  const loadSocieties = async () => {
    try {
      const response = await axios.get('/stores', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data) {
        setSocieties(response.data);
      }
    } catch (err) {
      console.error('載入社別列表失敗:', err);
    }
  };

  // 載入屆別列表
  const loadTerms = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (filterSsid) params.ssid = filterSsid;

      const response = await axios.get('/finance/terms', {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setTerms(response.data.data);
      }
    } catch (err) {
      setError('載入屆別列表失敗：' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // 初始化載入
  useEffect(() => {
    loadSocieties();
    loadTerms();
  }, [filterSsid]);

  // 開啟新增 Modal
  const handleAdd = () => {
    setModalMode('add');
    setCurrentTerm({
      term_id: null,
      ssid: user?.e_ssid || '',
      term_no: '',
      term_name: '',
      start_date: '',
      end_date: '',
      is_active: 1
    });
    setShowModal(true);
  };

  // 開啟編輯 Modal
  const handleEdit = (term) => {
    setModalMode('edit');
    setCurrentTerm({
      ...term,
      start_date: term.start_date.split('T')[0],
      end_date: term.end_date.split('T')[0]
    });
    setShowModal(true);
  };

  // 儲存屆別
  const handleSave = async () => {
    try {
      setError(null);
      setSuccess(null);

      if (!currentTerm.ssid || !currentTerm.term_no || !currentTerm.term_name ||
          !currentTerm.start_date || !currentTerm.end_date) {
        setError('社別、屆數、屆名、起始日期和結束日期為必填欄位');
        return;
      }

      if (modalMode === 'add') {
        const response = await axios.post('/finance/terms', currentTerm, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          setSuccess('屆別新增成功！');
          setShowModal(false);
          loadTerms();
        }
      } else {
        const response = await axios.put(`/finance/terms/${currentTerm.term_id}`, currentTerm, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          setSuccess('屆別更新成功！');
          setShowModal(false);
          loadTerms();
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || '儲存失敗');
    }
  };

  // 刪除屆別
  const handleDelete = async (id) => {
    if (!window.confirm('確定要刪除此屆別嗎？\n（若屆別已有預算或請款單，將無法刪除）')) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);

      const response = await axios.delete(`/finance/terms/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSuccess(response.data.message);
        loadTerms();
      }
    } catch (err) {
      setError(err.response?.data?.error || '刪除失敗');
    }
  };

  // 切換啟用/停用
  const toggleActive = async (term) => {
    try {
      const response = await axios.put(
        `/finance/terms/${term.term_id}`,
        { is_active: term.is_active === 1 ? 0 : 1 },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccess(`屆別已${term.is_active === 1 ? '停用' : '啟用'}`);
        loadTerms();
      }
    } catch (err) {
      setError(err.response?.data?.error || '操作失敗');
    }
  };

  // 格式化日期
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('zh-TW');
  };

  // 計算天數
  const calculateDays = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <Container fluid className="py-4">
      <div className="d-flex align-items-center mb-4">
        <h2 className="mb-0">💰 財務管理 - 屆別管理</h2>
        <Button
          variant="link"
          onClick={() => setShowHelpModal(true)}
          className="ms-2 text-decoration-none"
          title="查看說明"
        >
          <span style={{ fontSize: '1.5rem' }}>ℹ️</span>
        </Button>
      </div>

      {/* 錯誤/成功訊息 */}
      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess(null)} dismissible>{success}</Alert>}

      {/* 篩選工具列 */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <Form.Select
            value={filterSsid}
            onChange={(e) => setFilterSsid(e.target.value)}
            style={{ width: '300px' }}
          >
            <option value="">全部社別</option>
            {societies.map(society => (
              <option key={society.ss_id} value={society.ss_id}>
                {society.ss_name}
              </option>
            ))}
          </Form.Select>
        </div>

        <Button variant="primary" onClick={handleAdd}>
          ➕ 新增屆別
        </Button>
      </div>

      {/* 屆別列表 */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">載入中...</span>
          </div>
        </div>
      ) : (
        <Table striped bordered hover responsive>
          <thead className="table-dark">
            <tr>
              <th style={{ width: '150px' }}>社別</th>
              <th style={{ width: '80px' }}>屆數</th>
              <th style={{ width: '200px' }}>屆名</th>
              <th style={{ width: '120px' }}>起始日期</th>
              <th style={{ width: '120px' }}>結束日期</th>
              <th style={{ width: '100px' }}>天數</th>
              <th style={{ width: '80px' }}>狀態</th>
              <th style={{ width: '200px' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {terms.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center text-muted py-4">
                  目前沒有屆別資料
                </td>
              </tr>
            ) : (
              terms.map(term => {
                const days = calculateDays(term.start_date, term.end_date);
                const today = new Date();
                const startDate = new Date(term.start_date);
                const endDate = new Date(term.end_date);
                const isCurrent = today >= startDate && today <= endDate;

                return (
                  <tr key={term.term_id} className={isCurrent ? 'table-warning' : ''}>
                    <td>{term.society_name}</td>
                    <td className="text-center">
                      <Badge bg="primary">第 {term.term_no} 屆</Badge>
                    </td>
                    <td>{term.term_name}</td>
                    <td>{formatDate(term.start_date)}</td>
                    <td>{formatDate(term.end_date)}</td>
                    <td className="text-center">{days} 天</td>
                    <td>
                      <Badge bg={term.is_active === 1 ? 'success' : 'secondary'}>
                        {term.is_active === 1 ? '啟用' : '停用'}
                      </Badge>
                      {isCurrent && (
                        <Badge bg="warning" className="ms-1">進行中</Badge>
                      )}
                    </td>
                    <td>
                      <ButtonGroup size="sm">
                        <Button variant="outline-primary" onClick={() => handleEdit(term)}>
                          編輯
                        </Button>
                        <Button
                          variant={term.is_active === 1 ? 'outline-warning' : 'outline-success'}
                          onClick={() => toggleActive(term)}
                        >
                          {term.is_active === 1 ? '停用' : '啟用'}
                        </Button>
                        <Button variant="outline-danger" onClick={() => handleDelete(term.term_id)}>
                          刪除
                        </Button>
                      </ButtonGroup>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      )}

      {/* 新增/編輯 Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{modalMode === 'add' ? '新增屆別' : '編輯屆別'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>社別 <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={currentTerm.ssid}
                    onChange={(e) => setCurrentTerm({ ...currentTerm, ssid: e.target.value })}
                    disabled={modalMode === 'edit'}
                  >
                    <option value="">請選擇社別</option>
                    {societies.map(society => (
                      <option key={society.ss_id} value={society.ss_id}>
                        {society.ss_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>屆數 <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="例如: 1"
                    value={currentTerm.term_no}
                    onChange={(e) => setCurrentTerm({ ...currentTerm, term_no: e.target.value })}
                    min="1"
                  />
                  <Form.Text className="text-muted">
                    輸入第幾屆（數字）
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>屆名 <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="例如: 第 1 屆"
                value={currentTerm.term_name}
                onChange={(e) => setCurrentTerm({ ...currentTerm, term_name: e.target.value })}
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>起始日期 <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    value={currentTerm.start_date}
                    onChange={(e) => setCurrentTerm({ ...currentTerm, start_date: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>結束日期 <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    value={currentTerm.end_date}
                    onChange={(e) => setCurrentTerm({ ...currentTerm, end_date: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            {currentTerm.start_date && currentTerm.end_date && (
              <Alert variant="info">
                <strong>期間：</strong>
                共 {calculateDays(currentTerm.start_date, currentTerm.end_date)} 天
                （{formatDate(currentTerm.start_date)} 至 {formatDate(currentTerm.end_date)}）
              </Alert>
            )}

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="啟用此屆別"
                checked={currentTerm.is_active === 1}
                onChange={(e) => setCurrentTerm({ ...currentTerm, is_active: e.target.checked ? 1 : 0 })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            取消
          </Button>
          <Button variant="primary" onClick={handleSave}>
            {modalMode === 'add' ? '新增' : '更新'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 說明 Modal */}
      <Modal show={showHelpModal} onHide={() => setShowHelpModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>📌 屆別管理說明</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ul className="mb-0">
            <li>每個社別可以有多個屆別（例如：第 1 屆、第 2 屆...）</li>
            <li>每個屆別需要設定起始日期和結束日期</li>
            <li>屆別日期不可重疊</li>
            <li>系統會根據當前日期自動判斷當前屆別</li>
            <li>已有預算或請款單的屆別無法刪除</li>
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHelpModal(false)}>
            關閉
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default TermManagement;
